<template>
  <div class="sidebar-pinned">
    <Navbar />
    <main class="admin-main">
      <QrScannerModal :open="scannerOpen" @close="scannerOpen = false" @scanned="onScanned" />

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center" style="gap: 16px;">
              <div class="header-icon-wrap">
                <i class="fe fe-activity" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">
                  Hello,
                  <span v-if="user.staffprovider">
                    {{ user.staffprovider.agency_name }} </span>
                </h4>
                <small style="opacity: 0.8;">
                  {{ auth_user.job_title }} &middot; {{ auth_user.full_name }}
                </small>
              </div>
            </div>
            <div class="d-flex" style="gap: 8px;">
              <button class="btn btn-light btn-sm" @click="scannerOpen = true">
                <i class="fe fe-maximize mr-1"></i> Scan QR Code
              </button>
              <router-link to="/add-record">
                <button class="btn btn-outline-light btn-sm">
                  <i class="fe fe-file-plus mr-1"></i> New Record
                </button>
              </router-link>
              <button class="btn btn-outline-light btn-sm" @click="addRecordOffline" :disabled="isLoading">
                <i class="fe fe-download-cloud mr-1"></i> Sync Data
              </button>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -32px;">
        <div class="container pb-5">

          <!-- ── Stat Cards ── -->
          <div class="row mb-4">
            <div class="col-md-4 mb-3">
              <div class="card shadow stat-card h-100" style="border-left: 4px solid #2563eb;">
                <div class="card-body d-flex align-items-center py-3 px-4">
                  <div class="mr-3"
                    style="width: 44px; height: 44px; background: #eef2ff; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                    <i class="fe fe-clipboard" style="font-size: 20px; color: #2563eb;"></i>
                  </div>
                  <div>
                    <p class="mb-0 text-muted"
                      style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .05em;">My Records</p>
                    <h3 class="mb-0 font-weight-bold" style="line-height: 1.2;">
                      {{ records.meta && records.meta.total || '—' }}
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-4 mb-3">
              <div class="card shadow stat-card h-100" style="border-left: 4px solid #10b981;">
                <div class="card-body d-flex align-items-center py-3 px-4">
                  <div class="mr-3"
                    style="width: 44px; height: 44px; background: #ecfdf5; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                    <i class="fe fe-users" style="font-size: 20px; color: #10b981;"></i>
                  </div>
                  <div>
                    <p class="mb-0 text-muted"
                      style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .05em;">Patients</p>
                    <h3 class="mb-0 font-weight-bold" style="line-height: 1.2;">
                      {{ Array.isArray(clients) ? clients.length : '—' }}
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-4 mb-3">
              <router-link to="/all-referrals" class="text-decoration-none">
                <div class="card shadow stat-card h-100" style="border-left: 4px solid #f59e0b;">
                  <div class="card-body d-flex align-items-center py-3 px-4">
                    <div class="mr-3"
                      style="width: 44px; height: 44px; background: #fffbeb; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                      <i class="fe fe-shuffle" style="font-size: 20px; color: #f59e0b;"></i>
                    </div>
                    <div>
                      <p class="mb-0 text-muted"
                        style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .05em;">Incoming Referrals
                      </p>
                      <h3 class="mb-0 font-weight-bold" style="line-height: 1.2;">
                        {{ referrals && referrals.meta ? referrals.meta.total : '—' }}
                      </h3>
                    </div>
                    <div class="ml-auto">
                      <i class="fe fe-arrow-right text-muted" style="font-size: 1rem;"></i>
                    </div>
                  </div>
                </div>
              </router-link>
            </div>
          </div>

          <!-- ── Patient Records Table ── -->
          <div class="card shadow table-card">
            <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
              <div class="d-flex align-items-center">
                <i class="fe fe-clipboard text-primary mr-2"></i>
                <h6 class="mb-0 font-weight-bold">Patient Records</h6>
                <span v-if="records.meta" class="badge badge-soft-info ml-2">
                  {{ records.meta.total }}
                </span>
              </div>
              <router-link to="/add-record">
                <button class="btn btn-submit btn-sm">
                  <i class="fe fe-plus mr-1"></i> Add Record
                </button>
              </router-link>
            </div>
            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead class="table-head">
                  <tr>
                    <th class="table-th">Encounter ID</th>
                    <th class="table-th">Date of Visit</th>
                    <th class="table-th">Patient Name</th>
                    <th class="table-th">Patient ID</th>
                    <th class="table-th"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in records.data" :key="record.id" class="table-row">
                    <td class="px-4 py-3">
                      <span class="code-chip">{{ record.encounter_id }}</span>
                    </td>
                    <td class="py-3" style="font-size: 0.85rem;">
                      {{ record.date_of_visit | moment("MMM Do YYYY") }}
                    </td>
                    <td class="py-3">
                      <span v-if="record.patient != null" class="font-weight-bold" style="font-size: 0.85rem;">
                        {{ record.patient.full_name }}
                      </span>
                    </td>
                    <td class="py-3">
                      <span v-if="record.patient != null" class="d-flex align-items-center" style="gap: 6px;">
                        <span class="code-chip">{{ record.patient.id_card_number }}</span>
                        <button type="button" class="btn btn-sm btn-link p-0 text-muted" @click="copyText(record)"
                          title="Copy ID">
                          <i class="fe fe-copy" style="font-size: 0.8rem;"></i>
                        </button>
                      </span>
                    </td>
                    <td class="py-3">
                      <router-link v-if="record.service != null" :to="{ path: '/encounter/' + record.service.id }">
                        <button type="button" class="btn btn-outline-secondary btn-sm">
                          <i class="fe fe-eye"></i>
                        </button>
                      </router-link>
                    </td>
                  </tr>
                  <tr v-if="!records.data || !records.data.length">
                    <td colspan="5" class="text-center py-5 text-muted">
                      <i class="fe fe-clipboard" style="font-size: 2rem; opacity: 0.3;"></i>
                      <p class="mt-2 mb-0">No patient records found</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </section>

    </main>
    <Footer />
  </div>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import Footer from "@/views/Footer.vue";
  import Loading from "vue-loading-overlay";
  import QrScannerModal from "@/components/QrScannerModal.vue";
  import "vue-loading-overlay/dist/vue-loading.css";

  export default {
    components: { Navbar, Footer, Loading, QrScannerModal },

    data() {
      return {
        scannerOpen: false,
        auth_user: "",
        hospital_records: "",
        isLoading: false,
        fullPage: true,
        diseases: "",
        services: "",
        drugs: "",
        records: { meta: { total: "" } },
        clients: "",
        wallet: "",
        complaints: "",
        referrals: "",
        user: null,
      };
    },

    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/user`)
        .then((response) => { this.auth_user = response.data; })
        .catch((error) => { console.error(error); });
    },

    created() {
      this.getRecords();
      this.getClients();
      this.getDiagnosis();
      this.SetDB();
      this.getDataFromDb();
      this.getServices();
      this.getDrugs();
      this.getfacilityReferrals();
    },

    methods: {
      // An enrollee's QR code opens their verify page, where the encounter is started.
      onScanned({ type, id }) {
        this.scannerOpen = false;
        this.$router.push(`/verify-user/${type}/${id}`);
      },

      getRecords() {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.axios
          .get(`/api/v1/auth/professionalGetHealthRecord/${this.user.id}`)
          .then((response) => { this.records = response.data; })
          .catch((error) => { console.error(error); });
      },

      copyText(record) {
        navigator.clipboard.writeText(record.enrollee.id_card_number);
        this.$toasted.info("Copied to clipboard", { position: "top-center", duration: 3000 });
      },

      getDiagnosis() {
        this.axios
          .get(`/api/v1/auth/diagnosis-indexagencyPrimary/90`)
          .then((response) => { this.diseases = response.data; })
          .catch((error) => { console.error(error); });
      },

      getClients() {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.axios
          .get(`/api/v1/auth/getProviderToUser/${this.user.institutional_id}`)
          .then((response) => { this.clients = response.data; })
          .catch((error) => { console.error(error); });
      },

      async SetDB() {
        this.db = await this.getDb();
        this.hospital_records = await this.getDataFromDb();
        this.ready = true;
      },

      async getDb() {
        const DB_NAME = "hospital_data";
        const DB_VERSION = 1;
        return new Promise((resolve, reject) => {
          let request = window.indexedDB.open(DB_NAME, DB_VERSION);
          request.onerror = (e) => { console.log("Error opening db", e); reject("Error"); };
          request.onsuccess = (e) => { resolve(e.target.result); };
          request.onupgradeneeded = (e) => {
            let db = e.target.result;
            db.createObjectStore("hospital_records", { autoIncrement: true, keyPath: "id" });
          };
        });
      },

      async getDataFromDb() {
        return new Promise((resolve) => {
          let trans = this.db.transaction(["hospital_records"], "readonly");
          trans.oncomplete = () => { resolve(hospital_records); };
          let store = trans.objectStore("hospital_records");
          let hospital_records = [];
          store.openCursor().onsuccess = (e) => {
            let cursor = e.target.result;
            if (cursor) { hospital_records.push(cursor.value); cursor.continue(); }
          };
        });
      },

      async addRecordOffline() {
        this.isLoading = true;
        localStorage.setItem("clients", JSON.stringify(this.clients));
        localStorage.setItem("diseases", JSON.stringify(this.diseases));
        localStorage.setItem("services", JSON.stringify(this.services));
        localStorage.setItem("drugs", JSON.stringify(this.drugs));
        this.isLoading = false;
        this.$toasted.info("Data synced successfully!", { position: "top-center", duration: 3000 });
      },

      async addCatToDb(hospital_records) {
        return new Promise((resolve) => {
          let trans = this.db.transaction(["hospital_records"], "readwrite");
          trans.oncomplete = () => { resolve(); };
          let store = trans.objectStore("hospital_records");
          store.add(hospital_records);
        });
      },

      getDrugs() {
        this.axios
          .get(`/api/v1/auth/drug-agency/90`)
          .then((response) => { this.drugs = response.data; })
          .catch((error) => { console.error(error); });
      },

      getServices() {
        this.axios
          .get(`/api/v1/auth/service-agency/90`)
          .then((response) => { this.services = response.data; })
          .catch((error) => { console.error(error); });
      },

      getfacilityReferrals() {
        this.axios
          .get(`/api/v1/auth/referrals-secondary/${this.user.institutional_id}`)
          .then((response) => { this.referrals = response.data; this.isLoading = false; })
          .catch((error) => { console.error(error); });
      },
    },
  };
</script>

<style scoped>
  .stat-card {
    border-radius: 12px;
    transition: transform 0.15s, box-shadow 0.15s;
  }

  .stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1) !important;
  }
</style>